import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Briefcase, DollarSign, FileText, Pencil, User,
  ArrowRight, CheckCircle, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_CONFIG = {
  deal_funded:         { icon: Briefcase,     color: 'text-blue-500 bg-blue-50 border-blue-200',   badge: 'bg-blue-100 text-blue-700' },
  deal_paid_off:       { icon: CheckCircle,   color: 'text-green-500 bg-green-50 border-green-200', badge: 'bg-green-100 text-green-700' },
  deal_defaulted:      { icon: AlertTriangle, color: 'text-red-500 bg-red-50 border-red-200',       badge: 'bg-red-100 text-red-700' },
  deal_status_changed: { icon: ArrowRight,    color: 'text-amber-500 bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700' },
  deal_assigned:       { icon: Briefcase,     color: 'text-indigo-500 bg-indigo-50 border-indigo-200', badge: 'bg-indigo-100 text-indigo-700' },
  commission_earned:   { icon: DollarSign,    color: 'text-emerald-500 bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  commission_adjusted: { icon: DollarSign,    color: 'text-orange-500 bg-orange-50 border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  document_uploaded:   { icon: FileText,      color: 'text-violet-500 bg-violet-50 border-violet-200', badge: 'bg-violet-100 text-violet-700' },
  document_deleted:    { icon: FileText,      color: 'text-slate-500 bg-slate-50 border-slate-200',   badge: 'bg-slate-100 text-slate-600' },
  note_added:          { icon: Pencil,        color: 'text-teal-500 bg-teal-50 border-teal-200',     badge: 'bg-teal-100 text-teal-700' },
  contact_created:     { icon: User,          color: 'text-slate-500 bg-slate-50 border-slate-200',  badge: 'bg-slate-100 text-slate-600' },
  default:             { icon: User,          color: 'text-slate-500 bg-slate-50 border-slate-200',  badge: 'bg-slate-100 text-slate-600' },
};

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return format(d, 'h:mm a');
  } catch { return ''; }
}

function parseDateSafe(ts) {
  if (!ts) return null;
  try { return new Date(ts); } catch { return null; }
}

function groupByDate(events) {
  const groups = [];
  let currentLabel = null;
  let currentGroup = [];

  events.forEach(ev => {
    const d = parseDateSafe(ev.timestamp);
    let label;
    if (!d) {
      label = 'Unknown date';
    } else if (isToday(d)) {
      label = 'Today';
    } else if (isYesterday(d)) {
      label = 'Yesterday';
    } else {
      label = format(d, 'MMMM d, yyyy');
    }

    if (label !== currentLabel) {
      if (currentGroup.length) groups.push({ label: currentLabel, events: currentGroup });
      currentLabel = label;
      currentGroup = [ev];
    } else {
      currentGroup.push(ev);
    }
  });
  if (currentGroup.length) groups.push({ label: currentLabel, events: currentGroup });
  return groups;
}

function NoteEvent({ event }) {
  const [expanded, setExpanded] = useState(false);
  const { meta } = event;
  const isLong = meta?.truncated;
  return (
    <div>
      <p className="text-sm text-slate-700">
        {expanded ? (meta?.full_content || event.description) : event.description}
      </p>
      {isLong && (
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-xs text-blue-600 hover:underline mt-1 flex items-center gap-0.5"
          data-testid="note-expand-btn"
        >
          {expanded ? <><ChevronUp className="h-3 w-3" />Show less</> : <><ChevronDown className="h-3 w-3" />Read more</>}
        </button>
      )}
    </div>
  );
}

function TimelineEvent({ event }) {
  const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.default;
  const Icon = config.icon;
  return (
    <div className="flex gap-3" data-testid={`timeline-event-${event.type}`}>
      <div className="shrink-0 mt-0.5">
        <div className={`p-1.5 rounded-full border ${config.color}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <div className="flex-1 min-w-0 pb-4 border-b border-slate-100 last:border-0">
        {event.type === 'note_added' ? (
          <NoteEvent event={event} />
        ) : (
          <p className="text-sm text-slate-700">{event.description}</p>
        )}
        <p className="text-xs text-slate-400 mt-0.5">{formatTimestamp(event.timestamp)}</p>
      </div>
    </div>
  );
}

export default function ContactTimelineTab({ contactId, getAuthHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const loadEvents = async (append = false) => {
    const currentOffset = append ? offset : 0;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const res = await fetch(
        `${API}/contacts/${contactId}/timeline?limit=${LIMIT}&offset=${currentOffset}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const d = await res.json();
        setData(prev => append && prev
          ? { ...d, events: [...prev.events, ...d.events] }
          : d
        );
        setOffset(currentOffset + d.events.length);
      }
    } catch { /* silent */ }
    finally { setLoading(false); setLoadingMore(false); }
  };

  useEffect(() => { if (contactId) loadEvents(); }, [contactId]); // eslint-disable-line

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-400 text-sm">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600 mx-auto mb-2" />
        Loading timeline...
      </div>
    );
  }

  if (!data || data.events.length === 0) {
    return (
      <div className="py-16 text-center text-slate-400" data-testid="timeline-empty">
        <User className="h-8 w-8 mx-auto mb-2 opacity-25" />
        <p className="text-sm font-medium">No activity yet for this contact.</p>
        <p className="text-xs mt-1">Events will appear here as deals are assigned, documents are uploaded, and notes are added.</p>
      </div>
    );
  }

  const groups = groupByDate(data.events);

  return (
    <div className="space-y-6 mt-2" data-testid="contact-timeline">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{data.total} event{data.total !== 1 ? 's' : ''} total</p>
      </div>

      {groups.map(group => (
        <div key={group.label}>
          <div className="flex items-center gap-2 mb-3">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 shrink-0">{group.label}</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>
          <div className="space-y-0">
            {group.events.map((ev, i) => (
              <TimelineEvent key={`${ev.type}-${ev.timestamp}-${i}`} event={ev} />
            ))}
          </div>
        </div>
      ))}

      {data.has_more && (
        <div className="text-center pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadEvents(true)}
            disabled={loadingMore}
            data-testid="timeline-load-more-btn"
          >
            {loadingMore
              ? <><div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-400 border-t-transparent mr-2" />Loading...</>
              : `Load more (${data.total - offset} remaining)`}
          </Button>
        </div>
      )}
    </div>
  );
}
